import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Input } from '@Components/ui-v2/input';
import { Pager } from '@Components/ui-v2/pager';
import SegmentedControl from '@Components/ui-v2/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@Components/ui-v2/select';
import { FieldServiceListItem, Store } from '@Models';
import { assignFieldServiceColors } from '../../../FieldServices/assignFieldServiceColor';
import { getRetailerName } from '../../../FieldServices/constants';
import { SCHEDULE_NAME_MAX_LENGTH } from '../../constants';
import StepHeader from '../components/stepHeader';
import {
  FieldServiceCoverage,
  getStoreKey,
  getUncoveredStores,
  isFullyEligibleFieldService,
  isStoreCoveredByFieldService,
  SECTION_LABEL_CLASS_NAME,
} from '../constants';
import { ScheduleWizardFirmState, SelectedStoreKey, WizardFirmMode } from '../scheduleWizard.types';

interface Step4FirmProps {
  stores: Store[];
  selectedStores: SelectedStoreKey[];
  eligibleFieldServices: FieldServiceListItem[];
  fieldServiceCoverage: FieldServiceCoverage;
  isLoadingEligibility: boolean;
  hasEligibilityError: boolean;
  name: string;
  onNameChange: (name: string) => void;
  lacksSharedTaskType: boolean;
  value: ScheduleWizardFirmState;
  onChange: (value: ScheduleWizardFirmState) => void;
}

const MODE_OPTIONS: { value: WizardFirmMode; labelKey: string }[] = [
  { value: 'single', labelKey: 'Schedule.Wizard.Firm.ModeSingle' },
  { value: 'split', labelKey: 'Schedule.Wizard.Firm.ModeSplit' },
];

const SPLIT_PAGE_SIZE = 150;
const UNCOVERED_STORE_NAMES_CAP = 5;

const formatUncoveredStoreNames = (
  uncoveredStores: SelectedStoreKey[],
  storesByKey: Map<string, Store>,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const shownNames = uncoveredStores
    .slice(0, UNCOVERED_STORE_NAMES_CAP)
    .map((store) => storesByKey.get(getStoreKey(store))?.name ?? store.location_code);
  const remaining = uncoveredStores.length - shownNames.length;
  if (remaining <= 0) return shownNames.join(', ');
  return t('Schedule.Wizard.Firm.PartialCoverageMoreStores', { stores: shownNames.join(', '), count: remaining });
};

const Step4Firm = ({
  stores,
  selectedStores,
  eligibleFieldServices,
  fieldServiceCoverage,
  isLoadingEligibility,
  hasEligibilityError,
  name,
  onNameChange,
  lacksSharedTaskType,
  value,
  onChange,
}: Step4FirmProps) => {
  const { t } = useTranslation();

  const storesByKey = useMemo(() => new Map(stores.map((store) => [getStoreKey(store), store])), [stores]);
  const selectedStoreEntries = useMemo(
    () => selectedStores.map((key) => storesByKey.get(getStoreKey(key))).filter((store): store is Store => !!store),
    [selectedStores, storesByKey],
  );
  const fieldServiceColors = useMemo(
    () => assignFieldServiceColors(eligibleFieldServices.map((fieldService) => fieldService.id)),
    [eligibleFieldServices],
  );

  const [splitPage, setSplitPage] = useState(1);
  const splitPageCount = Math.max(1, Math.ceil(selectedStoreEntries.length / SPLIT_PAGE_SIZE));
  const currentSplitPage = Math.min(splitPage, splitPageCount);
  const splitPageStart = (currentSplitPage - 1) * SPLIT_PAGE_SIZE;
  const splitPageEntries = selectedStoreEntries.slice(splitPageStart, splitPageStart + SPLIT_PAGE_SIZE);

  const handleModeChange = (mode: WizardFirmMode) => {
    if (mode === value.mode) return;
    onChange({ ...value, mode });
  };

  const handleSingleSelect = (fieldServiceId: string) => onChange({ ...value, singleFieldServiceId: fieldServiceId });

  const handleAssignStore = (storeKey: string, fieldServiceId: string) => {
    onChange({ ...value, storeAssignments: { ...value.storeAssignments, [storeKey]: fieldServiceId } });
  };

  const handleClearAssignment = (storeKey: string) => {
    const nextAssignments = { ...value.storeAssignments };
    delete nextAssignments[storeKey];
    onChange({ ...value, storeAssignments: nextAssignments });
  };

  const hasNoEligibleFieldServices = !isLoadingEligibility && eligibleFieldServices.length === 0;

  return (
    <div className="flex flex-col gap-5">
      <StepHeader title={t('Schedule.Wizard.Firm.Title')} subtitle={t('Schedule.Wizard.Firm.SectionSubtitle')} />

      <div className="flex flex-col gap-1.5">
        <span className={cn(SECTION_LABEL_CLASS_NAME, "after:ml-0.5 after:text-error-600 after:content-['*']")}>
          {t('Schedule.Wizard.Firm.NameLabel')}
        </span>
        <Input
          aria-label={t('Schedule.Wizard.Firm.NameLabel')}
          type="text"
          required
          maxLength={SCHEDULE_NAME_MAX_LENGTH}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
        {name.trim() === '' && <p className="text-xs text-error-600">{t('Schedule.Wizard.Firm.NameRequired')}</p>}
      </div>

      {isLoadingEligibility ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3.5 text-xs text-gray-600">
          <Loader2 size={16} className="shrink-0 animate-spin" />
          {t('Schedule.Wizard.Firm.LoadingEligible')}
        </div>
      ) : hasEligibilityError ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-error-200 bg-error-50 px-4 py-3.5 text-xs text-error-700">
          <AlertTriangle size={16} className="shrink-0" />
          {t('Schedule.Wizard.Firm.EligibilityError')}
        </div>
      ) : hasNoEligibleFieldServices ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-error-200 bg-error-50 px-4 py-3.5 text-xs text-error-700">
          <AlertTriangle size={16} className="shrink-0" />
          {t('Schedule.Wizard.Firm.NoEligible')}
        </div>
      ) : (
        <>
          {eligibleFieldServices.length > 1 && (
            <SegmentedControl
              ariaLabel={t('Schedule.Wizard.Firm.ModeSwitcherAriaLabel')}
              options={MODE_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
              value={value.mode}
              onChange={handleModeChange}
            />
          )}

          {lacksSharedTaskType && (
            <div className="flex items-center gap-2.5 rounded-lg border border-error-200 bg-error-50 px-4 py-3.5 text-xs text-error-700">
              <AlertTriangle size={16} className="shrink-0" />
              {t('Schedule.Wizard.Firm.NoSharedTaskType')}
            </div>
          )}

          {value.mode === 'single' && (
            <div className="flex flex-col gap-2">
              {eligibleFieldServices.map((fieldService) => {
                const isSelected = value.singleFieldServiceId === fieldService.id;
                const isFullyEligible = isFullyEligibleFieldService(fieldServiceCoverage, fieldService.id);
                const uncoveredStores = getUncoveredStores(fieldServiceCoverage, fieldService.id);
                return (
                  <div key={fieldService.id} className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => handleSingleSelect(fieldService.id)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors',
                        isSelected ? 'border-brand-300 bg-brand-50' : 'border-gray-200 bg-white hover:border-gray-300',
                      )}>
                      <span
                        className="h-[7px] w-[7px] shrink-0 rounded-full"
                        style={{ backgroundColor: fieldServiceColors[fieldService.id] }}
                      />
                      <span className="flex-1 text-sm font-semibold text-gray-900">{fieldService.name}</span>
                      {isSelected && <Check size={16} className="shrink-0 text-brand-600" />}
                    </button>
                    {!isFullyEligible && (
                      <div className="flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3.5 py-2 text-xs text-warning-700">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>
                          {t('Schedule.Wizard.Firm.PartialCoverageBanner', {
                            covered: selectedStores.length - uncoveredStores.length,
                            total: selectedStores.length,
                            stores: formatUncoveredStoreNames(uncoveredStores, storesByKey, t),
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {value.mode === 'split' && (
            <>
              <div className="flex flex-col gap-2">
                {splitPageEntries.map((store) => {
                  const key = getStoreKey(store);
                  const assignedId = value.storeAssignments[key];
                  const hasAnyCoverageForStore = eligibleFieldServices.some((fieldService) =>
                    isStoreCoveredByFieldService(fieldServiceCoverage, key, fieldService.id),
                  );
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3.5 py-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-900">
                          {store.name ?? store.location_code}
                        </div>
                        <div className="truncate text-xs text-gray-500">
                          {getRetailerName(store.retailer)}
                          {store.region ? ` · ${store.region}` : ''}
                        </div>
                      </div>
                      <Select value={assignedId ?? ''} onValueChange={(nextId) => handleAssignStore(key, nextId)}>
                        <SelectTrigger className="h-9 w-48 shrink-0 rounded-md border-gray-200 bg-white px-2.5 text-sm">
                          <SelectValue
                            placeholder={
                              hasAnyCoverageForStore
                                ? t('Schedule.Wizard.Firm.AssignPlaceholder')
                                : t('Schedule.Wizard.Firm.NoCoveragePlaceholder')
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {eligibleFieldServices.map((fieldService) => (
                            <SelectItem
                              key={fieldService.id}
                              value={fieldService.id}
                              disabled={!isStoreCoveredByFieldService(fieldServiceCoverage, key, fieldService.id)}>
                              {fieldService.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {assignedId && (
                        <button
                          type="button"
                          onClick={() => handleClearAssignment(key)}
                          aria-label={t('Schedule.Wizard.Firm.ClearAssignment')}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {splitPageCount > 1 && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs tabular-nums text-gray-500">
                    {t('Schedule.Wizard.Firm.SplitShowingRange', {
                      from: splitPageStart + 1,
                      to: splitPageStart + splitPageEntries.length,
                      total: selectedStoreEntries.length,
                    })}
                  </span>
                  <Pager
                    page={currentSplitPage}
                    pageCount={splitPageCount}
                    onPageChange={setSplitPage}
                    ariaLabel={t('Schedule.Wizard.Firm.SplitPagerAriaLabel')}
                    previousLabel={t('Schedule.Wizard.Firm.PreviousPage')}
                    nextLabel={t('Schedule.Wizard.Firm.NextPage')}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Step4Firm;
